import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.jsx';

const normalizeMenuNode = (node) => {
  const rawChildren = Array.isArray(node.children)
    ? node.children
    : Array.isArray(node.submenu)
      ? node.submenu
      : [];

  const children = rawChildren
    .filter((child) => child.visible !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((child) => normalizeMenuNode(child));

  return {
    ...node,
    children,
  };
};

const hasValidPath = (path) => !!path && path !== '#';

const getPlacement = (item, fallbackSide = 'right') => ({
  side: item?.placement?.side || fallbackSide,
  align: item?.placement?.align || 'start',
  sideOffset: item?.placement?.sideOffset ?? 4,
  alignOffset: item?.placement?.alignOffset ?? 0,
  collisionPadding: item?.placement?.collisionPadding ?? 8,
});

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileOpenNodes, setMobileOpenNodes] = useState({});
  const [openDesktopMenu, setOpenDesktopMenu] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [isLoadingMenu, setIsLoadingMenu] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const cargarMenu = async () => {
      try {
  const response = await fetch(`/data/menu.json?v=${Date.now()}`);
        const data = await response.json();
        
        // Filtrar items visibles y ordenar por 'order'
        const visibleItems = data.menuItems
          .filter((item) => item.visible !== false)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((item) => normalizeMenuNode(item));
        
        setMenuItems(visibleItems);
      } catch (error) {
        console.error('Error al cargar el menú:', error);
        // Fallback al menú por defecto en caso de error
        setMenuItems([
          { id: 'inicio', name: 'Inicio', path: '/', visible: true, order: 1, children: [] }
        ]);
      } finally {
        setIsLoadingMenu(false);
      }
    };
    
    cargarMenu();
  }, []);

  const handleMobileToggle = (nodeKey) => {
    setMobileOpenNodes((prev) => ({
      ...prev,
      [nodeKey]: !prev[nodeKey],
    }));
  };

  const renderDesktopSubmenuItems = (items, parentKey) => {
    return items.map((item, index) => {
      const nodeKey = `${parentKey}-${item.id || index}`;
      const hasChildren = item.children && item.children.length > 0;

      if (hasChildren) {
        const placement = getPlacement(item, 'right');
        return (
          <DropdownMenuSub key={item.id || nodeKey}>
            <DropdownMenuSubTrigger className="px-6 py-3 text-sm text-gray-700 data-[highlighted]:bg-[#0b742f] data-[highlighted]:text-white data-[state=open]:bg-[#0b742f] data-[state=open]:text-white hover:bg-[#0b742f] hover:text-white focus:bg-[#0b742f] focus:text-white whitespace-normal leading-snug">
              <span className="text-left whitespace-normal break-words">{item.name}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              side={placement.side}
              align={placement.align}
              sideOffset={placement.sideOffset}
              alignOffset={placement.alignOffset}
              collisionPadding={placement.collisionPadding}
              className="w-[21rem] rounded-lg border-0 bg-white p-2 shadow-xl"
            >
              {renderDesktopSubmenuItems(item.children, nodeKey)}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        );
      }

      if (hasValidPath(item.path)) {
        return (
          <DropdownMenuItem
            key={item.id || nodeKey}
            asChild
            className="cursor-pointer px-6 py-3 text-sm text-gray-700 data-[highlighted]:bg-[#0b742f] data-[highlighted]:text-white hover:bg-[#0b742f] hover:text-white focus:bg-[#0b742f] focus:text-white whitespace-normal leading-snug"
          >
            <Link to={item.path}>{item.name}</Link>
          </DropdownMenuItem>
        );
      }

      return (
        <DropdownMenuItem
          key={item.id || nodeKey}
          className="cursor-default px-6 py-3 text-sm text-gray-500"
        >
          {item.name}
        </DropdownMenuItem>
      );
    });
  };

  const renderMobileNodes = (items, parentKey = 'root', level = 0) => {
    return items.map((item, index) => {
      const nodeKey = `${parentKey}-${item.id || index}`;
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = !!mobileOpenNodes[nodeKey];
      const paddingClass = level === 0 ? 'px-4' : level === 1 ? 'px-6' : 'px-8';

      if (hasChildren) {
        return (
          <div key={item.id || nodeKey}>
            <button
              onClick={() => handleMobileToggle(nodeKey)}
              className={`w-full flex items-center justify-between ${paddingClass} py-3 font-medium hover:bg-gray-50 rounded-lg transition-colors duration-200 ${
                item.highlight === 'unesco' ? 'text-[#0077C8]' : 'text-[#0b742f]'
              }`}
            >
              <span className="text-left">{item.name}</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen && <div className="mt-1 space-y-1">{renderMobileNodes(item.children, nodeKey, level + 1)}</div>}
          </div>
        );
      }

      if (hasValidPath(item.path)) {
        return (
          <Link
            key={item.id || nodeKey}
            to={item.path}
            onClick={() => setIsMobileMenuOpen(false)}
            className={`block ${paddingClass} py-2 text-sm text-gray-600 hover:text-[#0b742f] hover:bg-gray-50 rounded-lg transition-colors duration-200`}
          >
            {item.name}
          </Link>
        );
      }

      return (
        <div key={item.id || nodeKey} className={`block ${paddingClass} py-2 text-sm text-gray-500`}>
          {item.name}
        </div>
      );
    });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-md'
      }`}
    >
      <div className="w-full">
        {/* Top bar */}
        <div className="bg-[#0b742f] text-white py-2 px-6">
          <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
            <span>Centro de Estudios e Investigaciones para el Desarrollo Docente</span>
            <span>+52 33 1061 8187</span>
          </div>
        </div>

        {/* Main navigation */}
        <nav className="px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src="https://recursos.cenid.org/img_cenid/cenid.png"
                alt="CENID Logo"
                className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-1">
              {isLoadingMenu ? (
                <div className="flex items-center gap-2 text-[#0b742f] px-4 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Cargando menú...</span>
                </div>
              ) : (
                menuItems.map((item, index) => (
                  <div
                    key={item.id || index}
                    className="relative"
                    onMouseLeave={() => setOpenDesktopMenu(null)}
                  >
                    {item.children && item.children.length > 0 ? (
                      <DropdownMenu
                        open={openDesktopMenu === (item.id || index)}
                        onOpenChange={(isOpen) => setOpenDesktopMenu(isOpen ? (item.id || index) : null)}
                      >
                        <DropdownMenuTrigger asChild>
                          <button
                            onMouseEnter={() => setOpenDesktopMenu(item.id || index)}
                            className={`px-4 py-2 font-medium transition-colors duration-200 flex items-center gap-1 text-sm ${
                              item.highlight === 'unesco'
                                ? 'text-[#0077C8] hover:text-[#005A9C]'
                                : 'text-[#0b742f] hover:text-[#e87729]'
                            }`}
                          >
                            {item.name}
                            <ChevronDown
                              className={`w-4 h-4 transition-transform duration-200 ${
                                openDesktopMenu === (item.id || index) ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                          onMouseEnter={() => setOpenDesktopMenu(item.id || index)}
                          side={getPlacement(item, 'bottom').side}
                          align={getPlacement(item, 'bottom').align}
                          sideOffset={getPlacement(item, 'bottom').sideOffset}
                          alignOffset={getPlacement(item, 'bottom').alignOffset}
                          collisionPadding={getPlacement(item, 'bottom').collisionPadding}
                          className="z-[70] w-[21rem] rounded-lg border-0 bg-white p-2 shadow-xl"
                        >
                          {renderDesktopSubmenuItems(item.children, `desktop-${item.id || index}`)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <Link
                        to={item.path}
                        className={`px-4 py-2 font-medium transition-colors duration-200 text-sm ${
                          item.highlight === 'unesco'
                            ? location.pathname === item.path
                              ? 'text-[#0077C8] font-bold'
                              : 'text-[#0077C8] hover:text-[#005A9C]'
                            : location.pathname === item.path
                              ? 'text-[#e87729]'
                              : 'text-[#0b742f] hover:text-[#e87729]'
                        }`}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-[#0b742f] p-2"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="px-4 py-4 space-y-2 max-h-[70vh] overflow-y-auto">
              {isLoadingMenu ? (
                <div className="flex items-center justify-center gap-2 text-[#0b742f] py-4">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Cargando menú...</span>
                </div>
              ) : (
                <div className="space-y-1">
                  {renderMobileNodes(menuItems)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;